import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  TextField
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

// Constants
const TIME_REGEX = /^([01]?\d|2[0-3])(:[0-5]?\d?)?$/;
const BASE_DATE = '2000-01-01T';
const HOURS_STEP = 1;

// Utility functions
const formatTimeString = (timeString: string): string => {
  const [hoursPart = ''] = timeString.split(':');
  const hours = hoursPart.padStart(2, '0');
  return `${hours}:00`;
};

const parseTimeValue = (distributionTime: string): Date | null => {
  try {
    if (!distributionTime) return null;
    
    if (TIME_REGEX.test(distributionTime)) {
      const timeString = formatTimeString(distributionTime);
      const date = new Date(`${BASE_DATE}${timeString}:00`);
      date.setMinutes(0, 0, 0);
      return date;
    }
    
    return null;
  } catch (error) {
    console.warn('Failed to parse time value:', distributionTime, error);
    return null;
  }
};

const formatTimeOutput = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, '0');
  return `${hours}:00`;
};

interface BirthdayCampaignSettingsProps {
  daysOffset: string;
  distributionTime: string;
  onDaysOffsetChange: (offset: string) => void;
  onDistributionTimeChange: (time: string) => void;
  disabled?: boolean;
}

const BirthdayCampaignSettings: React.FC<BirthdayCampaignSettingsProps> = ({
  daysOffset,
  distributionTime,
  onDaysOffsetChange,
  onDistributionTimeChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [timePickerOpen, setTimePickerOpen] = React.useState(false);
  
  const handleTimeChange = (newValue: Date | null) => {
    try {
      if (newValue && !isNaN(newValue.getTime())) {
        const alignedValue = new Date(newValue);
        alignedValue.setMinutes(0, 0, 0);
        onDistributionTimeChange(formatTimeOutput(alignedValue));
      } else {
        onDistributionTimeChange('');
      }
    } catch (error) {
      console.warn('Invalid time input:', error);
      onDistributionTimeChange('');
    }
  };
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('campaignExperimental.create.birthdaySettingsTitle')}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              type="number"
              label={t('campaignExperimental.create.daysOffset')}
              value={daysOffset}
              onChange={(e) => onDaysOffsetChange(e.target.value)}
              placeholder={t('campaignExperimental.create.daysOffsetPlaceholder')}
              helperText={t('campaignExperimental.create.daysOffsetHelp')}
              disabled={disabled}
              inputProps={{
                step: HOURS_STEP,
              }}
              sx={{ width: '100%' }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
              <TimePicker
                ampm={false}
                label={t('campaignExperimental.create.distributionTime')}
                open={timePickerOpen}
                onOpen={() => setTimePickerOpen(true)}
                onClose={() => setTimePickerOpen(false)}
                value={parseTimeValue(distributionTime)}
                onChange={handleTimeChange}
                disabled={disabled}
                minutesStep={60}
                shouldDisableTime={(value, view) => view === 'minutes' && value !== 0}
                renderInput={(renderProps) => (
                  <TextField
                    {...renderProps}
                    fullWidth
                    disabled={disabled}
                    helperText={t('campaignExperimental.create.distributionTimeHelp')}
                    onClick={() => setTimePickerOpen(true)}
                    sx={{
                      '& .MuiInputBase-input': {
                        cursor: 'pointer'
                      },
                      '& .MuiInputBase-root': {
                        cursor: 'pointer'
                      }
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default BirthdayCampaignSettings;
